import { Injectable, inject, OnInit } from '@angular/core';
import {
  loadRemoteModule,
  LoadRemoteModuleOptions,
} from '@angular-architects/module-federation';
import { CookieService } from 'ngx-cookie-service';
import { z } from 'zod';

import {
  ENVIRONMENT,
  SpinnerService,
} from '@digital-platform/rs-shared/services';

const ScriptOptions = z.object({
  type: z.enum(['script']).optional().default('script'),
  remoteEntry: z.string().optional(),
  remoteName: z.string(),
  exposedModule: z.string(),
});

type ScriptOptions = z.infer<typeof ScriptOptions>;

const EsmOptions = z.object({
  type: z.enum(['module']),
  remoteEntry: z.string(),
  exposedModule: z.string(),
});

type EsmOptions = z.infer<typeof EsmOptions>;

const ManifestOptions = z.object({
  type: z.enum(['manifest']),
  remoteName: z.string(),
  exposedModule: z.string(),
});

type ManifestOptions = z.infer<typeof ManifestOptions>;

const MfeConfig = z.union([ScriptOptions, EsmOptions, ManifestOptions]);

type MfeConfig = z.infer<typeof MfeConfig>;

@Injectable({
  providedIn: 'root',
})
export class LoadMfeService {
  private environment = inject(ENVIRONMENT);
  private cookieService = inject(CookieService);
  private spinner = inject(SpinnerService);
  private classicUiHostOverride?: string;

  // integer to force cache busting, should change every hour
  private cacheBust = Math.floor(new Date().getTime() / 1000 / 60 / 60);

  private v1Mfes: Record<string, MfeConfig> = {
    classicContent: {
      type: 'module',
      remoteEntry: `/content/remoteEntry.js?${this.cacheBust}`,
      exposedModule: './Module',
    },
  };

  constructor() {
    this.classicUiHostOverride = this.cookieService.get('rsClassicUiHost');
  }

  public async loadMfe(mfe: string): Promise<void> {
    let config = this.v1Mfes[mfe];

    if (!config) {
      throw new Error(
        `Unable to find config for requested Classic MFE '${mfe}'!`
      );
    }

    if (config.type === 'module' && config.remoteEntry) {
      // use replace to prevent multiple slashes in the url
      const host = this.classicUiHostOverride || this.environment.classicUiHost;
      const remoteEntry = `${host}/${config.remoteEntry}`.replace(
        /(^|[^:])\/\/+/g,
        (_match: string, before: string) => before + '/'
      );
      config = {
        ...config,
        remoteEntry,
      };
    } else if (config.type !== 'module') {
      config = {
        ...config,
        remoteName: 'classicContent',
      };
    }

    try {
      await loadRemoteModule(config as LoadRemoteModuleOptions);
    } catch (err) {
      console.error('Error loading remote module.', err);
    }
  }
}
